import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

import { User } from '../models/user.model';
import { Router } from '@angular/router';
import { Globals } from '../shared/globals';
import { SignupDto } from '../models/signup-dto.model';
import { Information } from '../models/information.model';
import { Feature } from '../models/feature.model';
import { Buffer } from 'buffer';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private usersUrl: string;
  private resetPasswordUrl: string;
  private _token: string;

  public username: string;
  public currency: string;

  constructor(
    private http: HttpClient,
    private router: Router,
    private globals: Globals
  ) {
    this.usersUrl = globals.apiUrl('users');
    this.resetPasswordUrl = globals.apiUrl('users/reset');
    this.router = router;
  }

  private str2ab(inputString: string): ArrayBuffer {
    const buffer = new ArrayBuffer(inputString.length * 2);
    const bufferView = new Uint16Array(buffer);
    for (let i = 0, strLen = inputString.length; i < strLen; i++) {
      bufferView[i] = inputString.charCodeAt(i);
    }
    return buffer;
  }

  private ab2str(buffer: ArrayBuffer): string {
    return String.fromCharCode.apply(null, new Uint16Array(buffer));
  }

  private setUserInformation(
    token: string,
    username: string,
    currency: string,
    userId: string
  ) {
    crypto.subtle
      .generateKey(
        {
          name: 'AES-GCM',
          length: 256,
        },
        true,
        ['encrypt', 'decrypt']
      )
      .then((result) => {
        crypto.subtle.exportKey('jwk', result).then((result2) => {
          const item1 = btoa(JSON.stringify(result2));
          const array = new Int32Array(10);

          crypto.getRandomValues(array);
          const item2 = btoa(JSON.stringify(array));

          crypto.subtle
            .encrypt({ name: 'AES-GCM', iv: array }, result, this.str2ab(token))
            .then((result3) => {
              const item3 = this.ab2str(result3);

              const information = new Information();
              information.A = item1;
              information.B = item2;
              information.C = item3;
              information.D = btoa(username);
              information.E = btoa(currency);
              information.F = btoa(userId);

              const userString = JSON.stringify(information);
              sessionStorage.setItem('item1', userString);
            });
        });
      });

    this._token = token;
    this.username = username;
    this.currency = currency;
    this.userId = userId;
  }

  public getToken(): Promise<string> {
    if (
      this._token === undefined ||
      this._token === '' ||
      this._token === null
    ) {
      const item1 = JSON.parse(sessionStorage.getItem('item1'));

      if (
        item1 !== undefined &&
        item1 !== null &&
        item1.A !== undefined &&
        item1.A !== '' &&
        item1.A !== null &&
        item1.B !== undefined &&
        item1.B !== '' &&
        item1.B !== null &&
        item1.C !== undefined &&
        item1.C !== '' &&
        item1.C !== null
      ) {
        return Promise.resolve(
          crypto.subtle.importKey(
            'jwk',
            JSON.parse(atob(item1.A)),
            {
              name: 'AES-GCM',
              length: 256,
            },
            true,
            ['encrypt', 'decrypt']
          )
        )
          .then((result) => {
            const arr32 = new Int32Array(10);
            const arr = JSON.parse(atob(item1.B));
            arr32[0] = arr[0];
            arr32[1] = arr[1];
            arr32[2] = arr[2];
            arr32[3] = arr[3];
            arr32[4] = arr[4];
            arr32[5] = arr[5];
            arr32[6] = arr[6];
            arr32[7] = arr[7];
            arr32[8] = arr[8];
            arr32[9] = arr[9];

            return Promise.resolve(
              crypto.subtle.decrypt(
                {
                  name: 'AES-GCM',
                  iv: arr32,
                },
                result,
                this.str2ab(item1.C)
              )
            )
              .then((result2) => {
                const token = this.ab2str(result2);
                this._token = token;
                this.username = atob(item1.D);
                this.currency = atob(item1.E);
                this.userId = atob(item1.F);
                return Promise.resolve(token);
              })
              .catch((error) => Promise.reject(error));
          })
          .catch((error) => Promise.reject(error));
      } else {
        return Promise.reject('');
      }
    } else {
      return Promise.resolve(this._token);
    }
  }

  public isAuthenticated(): Promise<boolean> {
    return this.getToken()
      .then((result) => {
        if (result !== '' && result !== null) {
          return true;
        } else {
          return false;
        }
      })
      .catch((err) => {
        return false;
      });
  }

  get userId(): string {
    try {
      const id = sessionStorage.getItem('usr_id');
      if (id == null) {
        return '';
      }
      return id;
    } catch (error) {
      return '';
    }
  }

  set userId(id: string) {
    sessionStorage.setItem('usr_id', id);
  }

  get features(): Feature[] {
    try {
      const id = sessionStorage.getItem('usr_id');
      if (id == null) {
        return new Array<Feature>();
      }

      const featuresJson = sessionStorage.getItem(
        'features:' + id.toLowerCase()
      );

      const features: Array<Feature> = JSON.parse(featuresJson);
      return features;
    } catch (ex) {
      return new Array<Feature>();
    }
  }

  public hasFeatures(types: string[]): boolean {
    if (!types) {
      return true;
    }

    for (const type of types) {
      if (!this.hasFeature(type)) {
        return false;
      }
    }

    return true;
  }

  public hasFeature(feature: string): boolean {
    const features = this.features;
    if (features === null || features === undefined || features.length === 0) {
      return false;
    }
    return (
      features.find((f) => f.Type.toLowerCase() === feature.toLowerCase()) !==
      undefined
    );
  }

  public hasPermission(path: string): boolean {
    if (this.username === 'ecarup@smart-me.com') {
      return true;
    } else {
      if (path !== 'sample' && path !== 'rocketmaster') {
        return true;
      }
    }
    return false;
  }

  private createToken(username: string, password: string): string {
    let token = '';

    if (username !== '' && password !== '') {
      token = Buffer.from(username + ':' + password, 'utf8').toString('base64');
    }

    return token;
  }

  public logout() {
    this._token = '';
    this.username = '';
    this.currency = '';
    this.userId = '';
    sessionStorage.clear();
    this.router.navigate(['/']);
  }

  public login(username: string, password: string): Promise<User> {
    const token = this.createToken(username, password);

    const basicToken = 'Basic ' + token;

    return this.http
      .get(this.usersUrl, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: basicToken,
        },
      })
      .toPromise()
      .then((response) => {
        const user = response as User;

        const featuresJson = JSON.stringify(user.Features);
        sessionStorage.setItem(
          'features:' + user.IdAsString.toLowerCase(),
          featuresJson
        );

        this.setUserInformation(
          token,
          user.Email,
          user.PriceUnit,
          user.IdAsString
        );
      })
      .catch((error) => {
        this._token = '';
        this.username = '';
        this.currency = '';
        this.userId = '';
        sessionStorage.clear();
        return this.handleError(error);
      });
  }

  public signup(dto: SignupDto): Promise<void> {
    const url = `${this.usersUrl}`;
    return this.http
      .post<void>(url, dto, {
        headers: { 'Content-Type': 'application/json' },
      })
      .toPromise();
  }

  public resetPassword(email: string) {
    return this.http
      .post(this.resetPasswordUrl + '?email=' + encodeURIComponent(email), '', {
        headers: { 'Content-Type': 'application/json' },
      })
      .toPromise();
  }

  getUser(): Promise<User> {
    return this.getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .get(this.usersUrl, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((error) => {
            return error;
          });
      })
      .catch((err) => {
        Promise.reject('');
      });
  }

  updateUser(user: User): Promise<any> {
    return this.getToken()
      .then((result) => {
        const basicToken = 'Basic ' + result;
        return this.http
          .put(this.usersUrl, user, {
            headers: {
              'Content-Type': 'application/json',
              Authorization: basicToken,
            },
          })
          .toPromise()
          .then((response) => {
            return response;
          })
          .catch((error) => {
            return error;
          });
      })
      .catch((err) => Promise.reject(err));
  }

  private handleError(error: any): Promise<any> {
    return Promise.reject(error.message || error);
  }

  /**
   * Headers attached to the request.
   * Basic authentication header is set using the given token.
   *
   * @private
   * @param {string} token
   * @returns {HttpHeaders}
   */
  public headers(token: string): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      Authorization: 'Basic ' + token,
    });
  }
}
